import React from 'react';
import { Drawer, Form, Button, Input, Space, message, Alert, Divider, List } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import FormInput from '../../../global/Forms/FormInput';
import {
  searchDynamicFieldListRow,
  createDynamicFieldListRow,
  updateDynamicFieldListRow,
  deleteDynamicFieldListRow,
} from '../../../../actions/settings/dynamicFieldList';
import { DynamicFieldList, DynamicFieldListRow } from '../../../../../types/dynamicFieldList';

type DynamicFieldListEditListRowsDrawerProps = {
  open: boolean;
  list: DynamicFieldList | null;
  onClose: () => void;
  onRowsUpdated?: () => void;
};

type CreateFormValues = {
  newKey: string;
  newValue: string;
};

type EditFormValues = {
  editValue: string;
};

const DynamicFieldListEditListRowsDrawer: React.FC<DynamicFieldListEditListRowsDrawerProps> = ({
  open,
  list,
  onClose,
  onRowsUpdated,
}) => {
  const { t } = useTranslation();
  const [searching, setSearching] = React.useState(false);
  const [updatePending, setUpdatePending] = React.useState(false);
  const [searchedRows, setSearchedRows] = React.useState<DynamicFieldListRow[]>([]);
  const [searchPerformed, setSearchPerformed] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [editingRowKey, setEditingRowKey] = React.useState<string | null>(null);
  const [createMode, setCreateMode] = React.useState(false);

  // Validation schemas
  const createValidationSchema = Yup.object().shape({
    newKey: Yup.string().required(t('validation:required')),
    newValue: Yup.string().required(t('validation:required')),
  });

  const editValidationSchema = Yup.object().shape({
    editValue: Yup.string().required(t('validation:required')),
  });

  // Form methods
  const createForm = useForm<CreateFormValues>({
    defaultValues: { newKey: '', newValue: '' },
    resolver: yupResolver(createValidationSchema),
    mode: 'onChange',
  });

  const editForm = useForm<EditFormValues>({
    defaultValues: { editValue: '' },
    resolver: yupResolver(editValidationSchema),
    mode: 'onChange',
  });

  // Reset state when drawer closes or list changes
  React.useEffect(() => {
    if (!open) {
      setSearchedRows([]);
      setSearchPerformed(false);
      setSearchTerm('');
      setEditingRowKey(null);
      setCreateMode(false);
      createForm.reset();
      editForm.reset();
    }
  }, [open, createForm, editForm]);

  const handleSearch = async () => {
    if (!list || !searchTerm.trim()) return;

    setSearching(true);
    setSearchPerformed(false);
    setSearchedRows([]);
    setEditingRowKey(null);

    try {
      const response = await searchDynamicFieldListRow(list.name, searchTerm);
      // API now returns an array of partial matches
      const rows = Array.isArray(response.data) ? response.data : [];
      setSearchedRows(rows);
      setSearchPerformed(true);
    } catch (error: any) {
      // If there's an error, set empty array
      setSearchedRows([]);
      setSearchPerformed(true);
      message.error(error.response?.data?.message || t('common:error'));
    } finally {
      setSearching(false);
    }
  };

  const handleCreate = async (values: CreateFormValues) => {
    if (!list) return;

    setUpdatePending(true);
    try {
      await createDynamicFieldListRow(list.name, values.newKey, values.newValue);
      message.success(t('dynamicFieldList:rowCreated'));
      createForm.reset();
      setCreateMode(false);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('common:error'));
    } finally {
      setUpdatePending(false);
    }
  };

  const handleUpdate = async (rowKey: string, values: EditFormValues) => {
    if (!list) return;

    setUpdatePending(true);
    try {
      await updateDynamicFieldListRow(list.name, rowKey, values.editValue);
      message.success(t('dynamicFieldList:rowUpdated'));
      // Update the row in the local array without re-searching
      setSearchedRows(prevRows =>
        prevRows.map(row => (row.key === rowKey ? { ...row, value: values.editValue } : row))
      );
      setEditingRowKey(null);
      editForm.reset();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('common:error'));
    } finally {
      setUpdatePending(false);
    }
  };

  const handleDelete = async (rowKey: string) => {
    if (!list) return;

    setUpdatePending(true);
    try {
      await deleteDynamicFieldListRow(list.name, rowKey);
      message.success(t('dynamicFieldList:rowDeleted'));
      // Remove the row from the local array without re-searching
      setSearchedRows(prevRows => prevRows.filter(row => row.key !== rowKey));
    } catch (error: any) {
      message.error(error.response?.data?.message || t('common:error'));
    } finally {
      setUpdatePending(false);
    }
  };

  const handleEditClick = (row: DynamicFieldListRow) => {
    setEditingRowKey(row.key);
    editForm.setValue('editValue', row.value);
  };

  const handleCancelEdit = () => {
    setEditingRowKey(null);
    editForm.reset();
  };

  if (!list) return null;

  return (
    <Drawer
      title={t('dynamicFieldList:editRows', { listName: list.name })}
      open={open}
      onClose={onClose}
      width={600}
      destroyOnClose
      className="drawer-submit"
    >
      <div className="drawer-submit__content">
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Search Section */}
          <div>
            <h4>{t('dynamicFieldList:searchRow')}</h4>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={t('dynamicFieldList:enterKey')}
                onPressEnter={handleSearch}
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={searching}
                disabled={!searchTerm.trim()}
              >
                {t('common:search')}
              </Button>
            </Space.Compact>
          </div>

          {/* Search Results */}
          {searchPerformed && (
            <div>
              {searchedRows.length > 10 && (
                <Alert
                  message={t('dynamicFieldList:tooManyResultsTitle')}
                  description={t('dynamicFieldList:tooManyResults', {
                    count: searchedRows.length,
                  })}
                  type="warning"
                  showIcon
                />
              )}
              {searchedRows.length === 0 && (
                <Alert
                  message={t('dynamicFieldList:noResultsFound')}
                  description={t('dynamicFieldList:noResultsMessage', { searchTerm })}
                  type="info"
                  showIcon
                />
              )}
              {searchedRows.length > 0 && searchedRows.length <= 10 && (
                <div>
                  <h4>
                    {t('dynamicFieldList:searchResults')} -{' '}
                    {t('dynamicFieldList:resultsFound', { count: searchedRows.length })}
                  </h4>
                  <List
                    dataSource={searchedRows}
                    renderItem={row => (
                      <List.Item key={row.key} className="dynamic-field-list__row-item">
                        {editingRowKey === row.key ? (
                          // Edit Mode for this row
                          <FormProvider {...editForm}>
                            <Form
                              onFinish={editForm.handleSubmit(values =>
                                handleUpdate(row.key, values)
                              )}
                              className="dynamic-field-list__row-edit-form"
                            >
                              <div className="dynamic-field-list__row-key">
                                <strong>{t('dynamicFieldList:key')}:</strong> {row.key}
                              </div>
                              <FormInput
                                name="editValue"
                                label={t('dynamicFieldList:newValue')}
                                placeholder={t('dynamicFieldList:enterNewValue')}
                                required
                                useHookForm
                              />
                              <Form.Item>
                                <Space>
                                  <Button
                                    type="primary"
                                    htmlType="submit"
                                    size="small"
                                    loading={updatePending}
                                    disabled={!editForm.formState.isValid}
                                  >
                                    {t('common:save')}
                                  </Button>
                                  <Button size="small" onClick={handleCancelEdit}>
                                    {t('common:cancel')}
                                  </Button>
                                </Space>
                              </Form.Item>
                            </Form>
                          </FormProvider>
                        ) : (
                          // View Mode for this row
                          <Space
                            direction="vertical"
                            className="dynamic-field-list__row-content"
                            size="small"
                          >
                            <div className="dynamic-field-list__row-key">
                              <strong>{t('dynamicFieldList:key')}:</strong> {row.key}
                            </div>
                            <div className="dynamic-field-list__row-value">
                              <strong>{t('dynamicFieldList:value')}:</strong> {row.value}
                            </div>
                            <Space className="dynamic-field-list__row-actions">
                              <Button
                                type="primary"
                                icon={<EditOutlined />}
                                onClick={() => handleEditClick(row)}
                                size="small"
                              >
                                {t('dynamicFieldList:editValue')}
                              </Button>
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDelete(row.key)}
                                size="small"
                                loading={updatePending}
                              >
                                {t('common:delete')}
                              </Button>
                            </Space>
                          </Space>
                        )}
                      </List.Item>
                    )}
                  />
                </div>
              )}
            </div>
          )}

          <Divider />

          {/* Create New Row Section */}
          <div>
            {!createMode ? (
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => setCreateMode(true)}
                style={{ width: '100%' }}
              >
                {t('dynamicFieldList:createNewRow')}
              </Button>
            ) : (
              <div>
                <h4>{t('dynamicFieldList:createNewRow')}</h4>
                <FormProvider {...createForm}>
                  <Form layout="vertical" onFinish={createForm.handleSubmit(handleCreate)}>
                    <FormInput
                      name="newKey"
                      label={t('dynamicFieldList:key')}
                      placeholder={t('dynamicFieldList:enterKey')}
                      required
                      useHookForm
                    />
                    <FormInput
                      name="newValue"
                      label={t('dynamicFieldList:value')}
                      placeholder={t('dynamicFieldList:enterValue')}
                      required
                      useHookForm
                    />
                    <Form.Item>
                      <Space>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={updatePending}
                          disabled={!createForm.formState.isValid}
                        >
                          {t('common:create')}
                        </Button>
                        <Button onClick={() => setCreateMode(false)}>{t('common:cancel')}</Button>
                      </Space>
                    </Form.Item>
                  </Form>
                </FormProvider>
              </div>
            )}
          </div>
        </Space>
      </div>
      <div className="drawer-submit__bottom">
        <Button onClick={onClose}>{t('common:close')}</Button>
      </div>
    </Drawer>
  );
};

export default DynamicFieldListEditListRowsDrawer;
